import { Link, useParams, Navigate } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import { solutions } from '@/data/solutions';
import { ArrowRight, Check, AlertTriangle } from 'lucide-react';

function Section({ children, className = '' }) {
  const [ref, inView] = useInView();
  return <section ref={ref} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`}>{children}</section>;
}

const productPaths = {
  'Mushin Factory OS': '/products/factory-os',
  'Mushin Vendor Connect': '/products/vendor-connect',
  'IoT & Machine Data': '/products/factory-os',
};

export default function SolutionPage() {
  const { slug } = useParams();
  const solution = solutions[slug];

  if (!solution) return <Navigate to="/" replace />;

  return (
    <div className="pt-16">
      <section className="relative py-24 lg:py-32 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Solutions</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-[#111827]" data-testid="solution-title">{solution.title}</h1>
          <p className="text-lg text-[#4B5563] mt-4 max-w-2xl">{solution.subtitle}</p>
          <p className="mt-6 text-[#4B5563] max-w-3xl leading-relaxed">{solution.description}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" className="bg-[#C41E3A] hover:bg-[#A11830] text-white px-7 py-3 rounded-full text-[15px] font-medium transition-colors inline-flex items-center gap-2" data-testid="solution-demo-cta">
              Book a Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border border-[#D1D5DB] hover:border-[#9CA3AF] text-[#374151] px-7 py-3 rounded-full text-[15px] font-medium transition-colors" data-testid="solution-quote-cta">
              Get a Quote
            </a>
          </div>
        </div>
      </section>

      <Section className="py-24 border-t border-[#F0F0F0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Challenges</p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#111827] mb-8">What you face today</h2>
              <ul className="space-y-4">
                {solution.challenges.map((c) => (
                  <li key={c} className="flex items-start gap-3">
                    <AlertTriangle className="w-4 h-4 text-[#9CA3AF] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-[15px] text-[#4B5563] leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Outcomes</p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#111827] mb-8">What Mushin delivers</h2>
              <ul className="space-y-4">
                {solution.outcomes.map((o) => (
                  <li key={o} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#C41E3A] mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <span className="text-[15px] text-[#4B5563] leading-relaxed">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <Section className="py-24 border-t border-[#F0F0F0] bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Relevant Products</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#111827] mb-8">Powered by</h2>
          <div className="flex flex-wrap gap-4">
            {solution.products.map((p) => (
              <Link key={p} to={productPaths[p]} className="bg-white border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-lg px-6 py-4 transition-colors group shadow-sm">
                <span className="font-heading font-semibold text-[#111827] group-hover:text-[#C41E3A] transition-colors">{p}</span>
              </Link>
            ))}
          </div>
        </div>
      </Section>

      <section className="py-24 text-center border-t border-[#F0F0F0] bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-heading text-3xl font-bold text-[#111827]">Get started with {solution.title.replace('Mushin ', '')}</h2>
          <p className="mt-4 text-[#4B5563]">Our team will help you identify the right configuration for your needs.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="bg-[#C41E3A] hover:bg-[#A11830] text-white px-7 py-3 rounded-full text-[15px] font-medium transition-colors" data-testid="solution-final-demo">Book a Demo</Link>
            <a href="mailto:sales@mushinlabs.com" className="border border-[#D1D5DB] hover:border-[#9CA3AF] text-[#374151] px-7 py-3 rounded-full text-[15px] font-medium transition-colors" data-testid="solution-final-quote">Get a Quote</a>
          </div>
        </div>
      </section>
    </div>
  );
}
